import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ListingCard from '@/components/ListingCard';
import { Heart, MessageSquare, LayoutGrid, Sparkles, Bell, Plus, Download, Shield, Trash2 } from 'lucide-react';
import Mascot from '@/components/Mascot';
import { toast } from 'sonner';
import MySavedSearches from '@/components/MySavedSearches';
import IdentityVerificationCard from '@/components/IdentityVerificationCard';
import ReferralCard from '@/components/ReferralCard';
import ThemeToggle from '@/components/ThemeToggle';
import { useTheme } from '@/context/ThemeContext';
import GlobeMap3D from '@/components/three/GlobeMap3D';
import { Tilt3D } from '@/components/Tilt3D';
import YieldHistory from '@/components/yield/YieldHistory';

export default function Dashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mine, setMine] = useState([]);
  const [favs, setFavs] = useState([]);
  const [matches, setMatches] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [convs, setConvs] = useState([]);

  useEffect(() => {
    api.get('/my/listings').then(r => setMine(r.data || []));
    api.get('/favorites').then(r => setFavs(r.data || []));
    api.get('/matches').then(r => setMatches(r.data || []));
    api.get('/notifications').then(r => setNotifs(r.data || []));
    api.get('/conversations').then(r => setConvs(r.data || []));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-end gap-4">
          <Mascot pose="welcome" size={100} className="hidden sm:block shrink-0 -mb-2"/>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Dashboard</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Ciao, {user?.name}.</h1>
            <p className="mt-2 text-muted-foreground">Ruolo: <b>{user?.role}</b>{user?.verified && ' · Verificato'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button className="rounded-full" onClick={()=>nav('/pubblica')}><Plus size={14} className="mr-2"/> Nuovo annuncio</Button>
          {user?.role === 'admin' && <Button variant="outline" className="rounded-full" onClick={()=>nav('/admin')}>Admin</Button>}
          {user?.role === 'super_admin' && <Button variant="outline" className="rounded-full" onClick={()=>nav('/admin')}>Admin Console</Button>}
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-[1.35fr_1fr] gap-5">
        <div data-testid="dashboard-globe">
          <GlobeMap3D listings={mine} height="360px" zoom={7} hudLabel="tuoi terreni sul globo" testId="dashboard-globe-canvas"/>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <StatBox I={LayoutGrid} l="I miei annunci" v={mine.length} id="mine"/>
          <StatBox I={Heart} l="Preferiti" v={favs.length} id="favs"/>
          <StatBox I={MessageSquare} l="Conversazioni" v={convs.length} id="convs"/>
          <StatBox I={Bell} l="Notifiche" v={notifs.filter(n=>!n.read).length} id="notifs"/>
        </div>
      </div>

      <Tabs defaultValue={params.get('tab') || 'mine'} className="mt-10">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="mine">I miei annunci</TabsTrigger>
          <TabsTrigger value="matches"><Sparkles size={12} className="mr-1.5"/> Suggeriti</TabsTrigger>
          <TabsTrigger value="stime" data-testid="dashboard-tab-stime">Le mie stime</TabsTrigger>
          <TabsTrigger value="favs">Preferiti</TabsTrigger>
          <TabsTrigger value="msgs">Messaggi</TabsTrigger>
          <TabsTrigger value="notifs">Notifiche</TabsTrigger>
        </TabsList>

        <TabsContent value="stime" className="mt-6">
          <YieldHistory/>
        </TabsContent>

        <TabsContent value="mine" className="mt-6">
          {mine.length===0 ? <Empty t="Nessun annuncio pubblicato" a="Pubblica il tuo primo terreno" to="/pubblica"/> :
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mine.map(l => (<div key={l.listing_id}>
                <ListingCard listing={l}/>
                <div className="mt-2 text-xs text-muted-foreground">Stato: <b>{l.status}</b> · {l.views||0} views</div>
              </div>))}
            </div>}
        </TabsContent>

        <TabsContent value="matches" className="mt-6">
          {matches.length===0 ? <Empty t="Nessun suggerimento ancora" a="Imposta le tue preferenze" to="/preferenze"/> :
            <>
              <div className="mb-4 flex justify-end"><Link to="/suggeriti" className="text-sm font-medium link-underline">Apri pagina completa →</Link></div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {matches.slice(0,6).map(l => (
                  <div key={l.listing_id}>
                    <ListingCard listing={l} showMatch/>
                    {l.match_reasons?.length > 0 && (
                      <ul className="mt-3 text-xs space-y-1">
                        {l.match_reasons.slice(0,3).map((r,i) => {
                          const isPlus = r.startsWith('+'); const isMinus = r.startsWith('-');
                          return <li key={i} className={isPlus?'text-foreground':isMinus?'text-destructive':'text-muted-foreground'}>{r}</li>;
                        })}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </>}
        </TabsContent>

        <TabsContent value="favs" className="mt-6">
          {favs.length===0 ? <Empty t="Nessun preferito" a="Esplora terreni" to="/esplora"/> :
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favs.map(l => <ListingCard key={l.listing_id} listing={l}/>)}
            </div>}
        </TabsContent>

        <TabsContent value="msgs" className="mt-6">
          {convs.length===0 ? <Empty t="Nessuna conversazione" a="Esplora terreni per contattare i proprietari" to="/esplora"/> :
            <div className="divide-y divide-border border border-border rounded-2xl overflow-hidden">
              {convs.map(c => (
                <Link key={c.conversation_id} to={`/messaggi/${c.conversation_id}`}
                      className="flex items-center justify-between px-5 py-4 hover:bg-secondary/50">
                  <div>
                    <div className="font-medium">{c.other_user?.name || '—'}</div>
                    <div className="text-sm text-muted-foreground line-clamp-1">{c.last_message}</div>
                  </div>
                  {c.unread>0 && <span className="text-xs px-2 py-1 rounded-full bg-accent text-accent-foreground font-bold">{c.unread}</span>}
                </Link>
              ))}
            </div>}
        </TabsContent>

        <TabsContent value="notifs" className="mt-6">
          {notifs.length===0 ? <Empty t="Nessuna notifica" a="Torna alla home" to="/"/> :
            <div className="divide-y divide-border border border-border rounded-2xl overflow-hidden">
              {notifs.map(n => (
                <div key={n.notification_id} className={`px-5 py-4 ${!n.read?'bg-secondary/50':''}`}>
                  <div className="text-sm">{n.content}</div>
                  <div className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString('it-IT')}</div>
                </div>
              ))}
            </div>}
        </TabsContent>
      </Tabs>

      {/* Verifica identità → Proprietario Verificato */}
      <div className="mt-16">
        <IdentityVerificationCard/>
      </div>

      {/* Programma referral */}
      <div className="mt-6">
        <ReferralCard mine={mine}/>
      </div>

      {/* Alert Terreno Salvato */}
      <div className="mt-16">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={16} className="text-terracotta"/>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta">Le mie ricerche salvate</div>
        </div>
        <h2 className="font-display text-2xl mb-1">Alert Terreno Salvato</h2>
        <p className="text-sm text-muted-foreground max-w-2xl mb-6">Ricevi un'email automatica ogni volta che compare un nuovo terreno che matcha una ricerca che hai salvato. Configura la ricerca in Esplora e clicca <b>Salva ricerca</b>.</p>
        <MySavedSearches/>
      </div>

      {/* Sezione preferenze tema */}
      <div className="mt-16 rounded-2xl glass-vp p-6 md:p-8" data-testid="theme-preferences-section">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-chartreuse"/>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Aspetto</div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl mb-1">Tema dell'interfaccia</h2>
            <p className="text-sm text-muted-foreground max-w-xl">
              Scegli come visualizzare AgriNexus. Il tema <b className="text-foreground">scuro</b> è ottimizzato per lunghe sessioni; il tema <b className="text-foreground">chiaro</b> è pensato per ambienti luminosi e stampe.
            </p>
            <p className="text-xs text-muted-foreground mt-2">Preferenza attuale: <b className="text-foreground capitalize">{theme === 'light' ? 'Chiaro' : 'Scuro'}</b> · salvata su questo dispositivo</p>
          </div>
          <ThemeToggle />
        </div>
      </div>

      {/* Sezione GDPR / privacy */}
      <div className="mt-16 rounded-2xl border border-border p-6 md:p-8 bg-secondary/30">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} className="text-muted-foreground"/>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Privacy e dati personali (GDPR)</div>
        </div>
        <h2 className="font-display text-2xl mb-2">I tuoi diritti sui dati</h2>
        <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Come previsto dal Regolamento UE 2016/679 (GDPR) puoi in qualsiasi momento esportare tutti i tuoi dati personali in formato leggibile (art. 20) o richiedere la cancellazione definitiva dell'account (art. 17 — diritto all'oblio).
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button variant="outline" className="rounded-full" data-testid="gdpr-export-btn"
            onClick={async () => {
              try {
                const { data } = await api.get('/users/me/export');
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `agrinexus-miei-dati-${new Date().toISOString().slice(0,10)}.json`;
                a.click();
                URL.revokeObjectURL(a.href);
                toast.success('Esportazione completata');
              } catch (e) { toast.error('Errore durante l\'esportazione'); }
            }}>
            <Download size={13} className="mr-2"/> Esporta i miei dati (JSON)
          </Button>
          <Button variant="outline" className="rounded-full border-terracotta/40 text-terracotta hover:bg-terracotta/10 hover:text-terracotta" data-testid="gdpr-delete-btn"
            onClick={async () => {
              const c1 = window.prompt('ATTENZIONE: questa azione è irreversibile. Verranno cancellati permanentemente il tuo account, tutti i tuoi annunci, messaggi, preferiti e dati.\n\nScrivi "CANCELLA" per confermare:');
              if (c1 !== 'CANCELLA') { toast.info('Cancellazione annullata'); return; }
              try {
                await api.delete('/users/me');
                try { localStorage.removeItem('agn_token'); localStorage.removeItem('agrinexus_token'); localStorage.removeItem('agrinexus_user'); } catch {}
                toast.success('Account cancellato');
                setTimeout(() => { window.location.href = '/'; }, 1000);
              } catch (e) { toast.error('Errore durante la cancellazione'); }
            }}>
            <Trash2 size={13} className="mr-2"/> Cancella il mio account
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Per altre richieste (rettifica, limitazione, opposizione) scrivi a <a className="underline hover:text-foreground" href="mailto:info@agrinexus.it?subject=Richiesta%20GDPR">info@agrinexus.it</a>. Rispondiamo entro 30 giorni come previsto dall'art. 12 GDPR.
        </p>
      </div>
    </div>
  );
}

function StatBox({ I, l, v, id }) {
  return (
    <Tilt3D className="rounded-2xl glass-vp border border-border p-5 relative overflow-hidden group" data-testid={`dashboard-stat-${id}`}>
      <div aria-hidden className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-chartreuse/10 blur-2xl transition-opacity duration-500 opacity-40 group-hover:opacity-100"/>
      <div style={{ transform: 'translateZ(28px)' }}>
        <I size={16} className="text-chartreuse" strokeWidth={1.6}/>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mt-3">{l}</div>
        <div className="font-display text-4xl mt-1">{v}</div>
      </div>
    </Tilt3D>
  );
}
function Empty({t,a,to}) {
  return (
    <div className="rounded-2xl border border-dashed border-border p-14 text-center">
      <h3 className="font-display text-2xl">{t}</h3>
      <Link to={to} className="mt-4 inline-block text-sm font-medium link-underline">{a} →</Link>
    </div>
  );
}
